import { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/page-header";
import { Input } from "@/components/ui/input";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useList } from "@/hooks/use-list";
import { api } from "@/lib/api";
import { Plus, Trash2 } from "lucide-react";

export default function JobOrdersPage() {
  const [q, setQ] = useState("");
  const qc = useQueryClient();
  const { data, isLoading } = useList<any>("/job-orders", { q, pageSize: 50 });

  async function del(id: string, title: string) {
    if (!confirm(`Delete job order "${title}"?`)) return;
    await api.delete(`/job-orders/${id}`);
    qc.invalidateQueries({ queryKey: ["/job-orders"] });
  }

  return (
    <>
      <PageHeader
        title="Job Orders"
        description="Open, hold, filled, and cancelled requisitions."
        actions={<Button asChild><Link to="/job-orders/new"><Plus className="mr-1 h-4 w-4"/>New Job Order</Link></Button>}
      />
      <div className="p-6 space-y-4">
        <Input placeholder="Search job orders…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" />
        <div className="rounded-md border bg-card">
          <Table>
            <Thead>
              <Tr>
                <Th>Title</Th><Th>Client</Th><Th>Skills</Th><Th>Location</Th><Th>Visa</Th><Th>Status</Th><Th className="w-10"></Th>
              </Tr>
            </Thead>
            <Tbody>
              {isLoading && <Tr><Td colSpan={7} className="py-8 text-center text-muted-foreground">Loading…</Td></Tr>}
              {!isLoading && (data?.data?.length ?? 0) === 0 && (
                <Tr><Td colSpan={7} className="py-8 text-center text-muted-foreground">No job orders yet.</Td></Tr>
              )}
              {data?.data?.map((j: any) => (
                <Tr key={j.id}>
                  <Td className="font-medium"><Link to={`/job-orders/${j.id}`} className="hover:underline">{j.title}</Link></Td>
                  <Td>{j.clientAccount?.name ?? "—"}</Td>
                  <Td className="text-xs text-muted-foreground">
                    {(j.skillsRequired ?? []).slice(0, 3).join(", ")}
                    {j.skillsRequired?.length > 3 ? ` +${j.skillsRequired.length - 3}` : ""}
                  </Td>
                  <Td>{j.location ?? j.locationType}</Td>
                  <Td className="text-muted-foreground">{j.visaRequirement ?? "—"}</Td>
                  <Td><Badge variant={j.status === "OPEN" ? "default" : "secondary"}>{j.status}</Badge></Td>
                  <Td>
                    <Button variant="ghost" size="icon" onClick={() => del(j.id, j.title)} aria-label="Delete">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </div>
      </div>
    </>
  );
}
